import { MouseEventHandler, useCallback } from 'react';
import {
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { CloseOutlined, OpenInNew } from '@mui/icons-material';
import useMapSearchItemStyles from './styles';
import BoldMatch from './BoldMatch';
import { useBoolean } from '@qfa/react-components';
import { useMapSearchHistory } from '../MapSearchHistory';
import { useMapBoxSearchContext } from '../MapSearchBoxContext';

const DeleteItemButton = ({ onClick }: { onClick: MouseEventHandler<HTMLButtonElement> }) => {
  const classes = useMapSearchItemStyles();
  return (
    <IconButton
      size='small'
      className={classes.deleteIconItem}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick(event);
      }}
    >
      <CloseOutlined />
    </IconButton>
  );
};

const NewWindowButton = ({ onClick }: { onClick: MouseEventHandler<HTMLButtonElement> }) => {
  const classes = useMapSearchItemStyles();
  return (
    <IconButton
      size='small'
      className={classes.newWindowIconItem}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick(event);
      }}
    >
      <OpenInNew />
    </IconButton>
  );
};

export interface MapSearchResultItemProps {
  name: string;
  onClick?: (openInNewWindow?: boolean) => void;
  icon: JSX.Element;
  onDelete?: () => void;
  clearInput?: boolean;
  additionalData?: string;
  newWindow?: boolean;
  saveToHistory?: boolean;
}

const MapSearchResultItem: React.FC<MapSearchResultItemProps> = ({
  name,
  onClick,
  onDelete,
  icon,
  clearInput = true,
  additionalData,
  newWindow = false,
  saveToHistory = true,
}) => {
  const classes = useMapSearchItemStyles();
  const [{ on: hover, off: blur }, isHovered] = useBoolean();

  const { searchText, searchBoxPosition, setSearchText } = useMapBoxSearchContext();

  const { addSearchHistory } = useMapSearchHistory();

  const onSearchItemClick = useCallback(
    (openInNewWindow = false) => {
      setSearchText(!clearInput ? name : '');
      saveToHistory && name?.length && addSearchHistory(name);
      onClick?.(openInNewWindow);
    },
    [addSearchHistory, clearInput, name, onClick, setSearchText, saveToHistory]
  );

  return (
    <ListItem disablePadding className={classes.listItem} onMouseLeave={blur} onMouseOver={hover}>
      <Tooltip
        title={`${name} ${additionalData ? ', ' : ''}${additionalData ?? ''}`}
        placement={
          searchBoxPosition === 'bottom-right' || searchBoxPosition === 'top-right'
            ? 'left'
            : 'right'
        }
      >
        <ListItemButton
          classes={{ root: classes.listItemButton }}
          onClick={() => onSearchItemClick(false)}
        >
          <ListItemIcon classes={{ root: classes.listItemIcon }}>
            <IconButton size='small' className={classes.itemIcon}>
              {icon}
            </IconButton>
          </ListItemIcon>
          <ListItemText
            secondary={additionalData}
            primary={<BoldMatch text={name} textToMatch={searchText} />}
            secondaryTypographyProps={{
              width: '100%',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          />
          {newWindow && isHovered && <NewWindowButton onClick={() => onSearchItemClick(true)} />}
          {onDelete && isHovered && <DeleteItemButton onClick={onDelete} />}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default MapSearchResultItem;
